import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiHome, FiBarChart2, FiAlertCircle, FiPlus, FiSend, FiFileText, FiMessageCircle, FiArrowRight, FiTrendingUp, FiUsers, FiRadio, FiGlobe, FiX } from 'react-icons/fi';
import { IGlobalState } from '../../model/IGlobalState';
import { DashboardOverviewResult } from '../../model/DashboardOverviewResult';
import api from '../../api/api';

const quickActions = (data: DashboardOverviewResult, ticker: string) => [
    { icon: <FiPlus />, title: 'Add an announcement', sub: 'Upload a PDF and email investors', url: `/client-stocks/${ticker}/Announcements?announcement=new` as string | null },
    { icon: <FiSend />, title: 'Send an email campaign', sub: 'Write a news update to subscribers', url: `/client-stocks/${ticker}/Email Marketing/Campaigns?campaign=new` },
    { icon: <FiFileText />, title: 'Run a holdings report', sub: 'See who bought or sold shares', url: `/client-stocks/${ticker}/Share Registry/Holdings Movement` },
    { icon: <FiMessageCircle />, title: 'Answer investor questions', sub: data.openQuestions ? `${data.openQuestions} open question${data.openQuestions === 1 ? '' : 's'}` : 'No open questions', url: `/AnnouncementComments/${ticker}` },
];

const ranges = ['1D', '1W', '1M', '3M', '1Y', '5Y'];

const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening'; };

const registryUpdated = (utc: string) => moment(utc).calendar(null, { sameDay: '[today], h:mm a', lastDay: '[yesterday], h:mm a', lastWeek: 'dddd, h:mm a', sameElse: 'D MMM, h:mm a' });

const PriceChart: React.FC<{ points: number[] }> = ({ points }) => {
    const w = 800, h = 160;
    const min = Math.min(...points), max = Math.max(...points);
    const pad = (max - min) * 0.25 || 0.01;
    const lo = min - pad, hi = max + pad;
    const pts = points.map((p, i) => [i * (w / (points.length - 1)), h - ((p - lo) / (hi - lo)) * h]);
    const line = pts.map(([x, y]) => `${x},${y}`).join(' ');
    const [lx, ly] = pts[pts.length - 1];
    return <svg className="price-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs><linearGradient id="v2-price-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00b5d1" stopOpacity="0.35" /><stop offset="100%" stopColor="#00b5d1" stopOpacity="0" />
        </linearGradient></defs>
        {[0, 0.25, 0.5, 0.75, 1].map(f => <line key={f} x1="0" x2={w} y1={f * h} y2={f * h} className="grid" />)}
        <polygon points={`0,${h} ${line} ${w},${h}`} fill="url(#v2-price-fill)" />
        <polyline points={line} className="line" />
        <line x1={lx} x2={lx} y1="0" y2={h} className="cursor" />
        <circle cx={lx} cy={ly} r="4" className="dot" />
    </svg>;
};

const Dashboard: React.FC = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [range, setRange] = useState('1M');
    const [data, setData] = useState<DashboardOverviewResult | null>(null);
    const history = useHistory();
    // relative url = in-app route (same tab); absolute = external page (new tab)
    const open = (url: string | null) => { if (url) url.startsWith('/') ? history.push(url) : window.open(url, '_blank'); };
    const firstName = user.username.split(/[@.]/)[0];
    const name = firstName.charAt(0).toUpperCase() + firstName.slice(1);

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stock) return;
        const result = await api().getDashboardOverview(stock.id);
        if (isMounted()) setData(result);
    }, [stock?.id]);

    const dismiss = async (key: string) => {
        if (!stock || !data) return;
        setData({ ...data, attention: data.attention.filter(x => x.key !== key) });
        await api().dismissAttention(stock.id, key);
    };

    const sp = data?.sharePrice;
    const up = (sp?.changePct ?? 0) >= 0;

    return <div className="dashboard-v2">
        <div className="sub-tabs">
            <button type="button" className="active"><FiHome /> Overview</button>
            <button type="button"><FiBarChart2 /> Analytics</button>
        </div>

        <div className="page-header">
            <h1>{greeting()}, {name}</h1>
            <p>{moment().format('dddd, D MMMM YYYY')}{stock && ` · ${stock.companyName} (${stock.ticker}:${stock.exchange})`}{data?.registryLastUpdated && ` · Registry last updated ${registryUpdated(data.registryLastUpdated)}`}</p>
        </div>

        {data && sp && <>
        <div className="row row-0">
            <section className="card attention">
                <header>
                    <span className="icon-tile"><FiAlertCircle /></span>
                    <h2>Needs Your Attention</h2>
                    <span className="pill warn">{data.attention.length ? `${data.attention.length} things to do today` : 'You’re all caught up'}</span>
                </header>
                {data.attention.map(x => <div className="item" key={x.key ?? x.title}>
                    <div className="text"><div className="title">{x.title}</div><div className="sub">{x.detail}</div></div>
                    <button type="button" className="btn-outline" onClick={() => open(x.url)}>{x.action}</button>
                    {x.key && <button type="button" className="dismiss" title="Dismiss" onClick={() => dismiss(x.key!)}><FiX /></button>}
                </div>)}
            </section>

            <section className="card quick-actions">
                <h2>Quick Actions</h2>
                <p className="sub">The things you do most often</p>
                {quickActions(data, `${stock!.ticker}:${stock!.exchange}`).map(x => <button type="button" className="action" key={x.title} onClick={() => open(x.url)}>
                    <span className="icon-tile dark">{x.icon}</span>
                    <span className="text"><span className="title">{x.title}</span><span className="sub">{x.sub}</span></span>
                    <FiArrowRight className="arrow" />
                </button>)}
            </section>
        </div>

        <section className="glance">
            <h2>Your workspace at a glance</h2>
            <div className="grid">
                {data.glance.map(x => <div className="stat" key={x.label}>
                    <div className="value">{x.value} <span className="label">{x.label}</span></div>
                    <div className="sub">{x.detail}</div>
                    <a href="#/" className="link">{x.link} <FiArrowRight /></a>
                </div>)}
            </div>
        </section>

        <div className="row row-2">
            <section className="card share-price">
                <header>
                    <span className="icon-tile"><FiTrendingUp /></span>
                    <h2>{sp.title}</h2>
                    <a href="#/" className="link">Open Share Price <FiArrowRight /></a>
                </header>
                <div className="price-row">
                    <span className="price">A${sp.price.toFixed(3)}</span>
                    <span className={'pill ' + (up ? 'ok' : 'warn')}>{up ? '▲' : '▼'} {Math.abs(sp.changePct).toFixed(1)}% ({up ? '+' : '-'}A${Math.abs(sp.changeAbs).toFixed(3)})</span>
                    <span className="delay">{sp.delay}</span>
                    <div className="ranges">{ranges.map(r => <button type="button" key={r} className={r === range ? 'active' : ''} onClick={() => setRange(r)}>{r}</button>)}</div>
                </div>
                <div className="chart-wrap">
                    <div className="y-axis">{sp.yLabels.map(v => <span key={v}>{v}</span>)}</div>
                    <PriceChart points={sp.points} />
                    <div className="tooltip"><strong>{sp.tooltip}</strong><span>{sp.tooltipDetail}</span></div>
                </div>
                <div className="x-axis">{sp.xLabels.map(v => <span key={v}>{v}</span>)}</div>
                <div className="stats">{sp.stats.map(x => <div className="stat" key={x.label}><span className="k">{x.label}</span><span className="v">{x.value}</span></div>)}</div>
            </section>

            <section className="card top-holders">
                <header>
                    <span className="icon-tile"><FiUsers /></span>
                    <h2>Top Shareholders</h2>
                    <a href="#/" className="link">All {data.topHolders.total} <FiArrowRight /></a>
                </header>
                {data.topHolders.items.length === 0 && <p className="sub">No registry data yet</p>}
                {data.topHolders.items.map((x, i) => <div className="holder" key={i}>
                    <span className="rank">{x.rank}</span>
                    <span className="name">{x.name}</span>
                    <span className="pct">{x.pct.toFixed(1)}%</span>
                    <span className="bar"><span style={{ width: `${(x.pct / (data.topHolders.items[0].pct || 1)) * 100}%` }} /></span>
                </div>)}
            </section>
        </div>

        <div className="row row-3">
            <section className="card announcements">
                <header>
                    <span className="icon-tile"><FiRadio /></span>
                    <h2>Recent Announcements</h2>
                    <a href="#/" className="link">All {data.announcements.total} <FiArrowRight /></a>
                </header>
                {data.announcements.items.map(x => <div className="item" key={x.headline}>
                    <div className="text">
                        <div className="title">{x.headline}</div>
                        <div className={'sub' + (x.priceSensitive ? ' sensitive' : '')}>{x.when}{x.priceSensitive && <> · Price sensitive</>}</div>
                    </div>
                    <span className={'pill ' + x.tone}>{x.badge}</span>
                </div>)}
            </section>

            <section className="card investors">
                <header>
                    <span className="icon-tile"><FiGlobe /></span>
                    <h2>Latest from Investors</h2>
                    <a href="#/" className="link">More <FiArrowRight /></a>
                </header>
                {data.investors.map(x => <div className="item" key={x.text}>
                    <div className="title">{x.text}</div>
                    <div className="sub">{x.meta}</div>
                </div>)}
            </section>
        </div>
        </>}
    </div>;
};

export default Dashboard;
